import React from 'react';
import styled from 'styled-components';
import { Paper } from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';

import { StaticImageUiSketchItem, UiCardItemDb } from '../redux/uiCardItemDbSlice';
import { FontType } from '../redux/cardAttributeModelIndex';
import useOneCardDb from '../hooks/useOneCardDb';
import useUiCardItemDbs from '../hooks/useUiCardItemDbs';
import useImageData from '../hooks/useImageData'
import { deepCopy } from '../general';
import { initial } from './index';
import { EditUiCardItemDbHashType } from '../components/EditDialog';

type CardWrapperProps = {
  width: number;
  height: number;
  $frameDisplay?: boolean;
  $frameColor?: string;
  scale: number;
}
const CardWrapper = styled(Paper)<CardWrapperProps>`
  position: relative;
  overflow: hidden;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  border: ${props => props.$frameDisplay ? '1px solid ' + props.$frameColor : undefined};
  box-sizing: border-box;
`
type CardPaperProps = {
  width: number;
  height: number;
  scale: number;
}
const CardPaper = styled(Paper)<CardPaperProps>`
  position: absolute;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  transform: ${props => 'scale(' + props.scale + ')'};
  transform-origin: top left;
`
type UiPartsDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
}
const UiPartsDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
  white-space: nowrap;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
type UiPartsInputDivProps = UiPartsDivProps & {
  labelVisibility: boolean;
  size: number;
};
const UiPartsInputDiv = styled.div<UiPartsInputDivProps>`
  position: absolute;
  display: grid;
  grid-template-rows: ${props => props.labelVisibility ? (props.size + props.size / 2) + 'px auto' : undefined};
  left: 0;
  top: 0;
  overflow: visible;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
const UiPartsCheckboxDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
  display: flexbox;
  align-items: center;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
const UiPartsVariableImageDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  box-sizing: border-box;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
const UiPartsStaticImageDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  box-sizing: border-box;
  transform: ${props => {
    if (props.width < 0) return 'translate(' + (props.x + props.width) + 'px, ' + props.y + 'px) scaleX(-1) !important';
    return 'translate(' + props.x + 'px, ' + props.y + 'px) !important';
  }};
  transform-origin: center;
  width: ${props => Math.abs(props.width) + 'px'};
  height: ${props => props.height + 'px'};
`
type TextDivProps = {
  flame: boolean;
  flameColor: string;
  backgroundOpacity: number;
  backgroundColor: string;
} & {[P in keyof FontType]?: FontType[P]};
const TextDiv = styled.div<TextDivProps>`
  display: grid;
  overflow-wrap: anywhere;
  width: inherit;
  overflow: hidden;
  border: ${props => props.flame ? 'solid 4px ' + props.flameColor : undefined};
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  background-color: ${props => {
    const hexArray = props.backgroundColor.split('');
    const r = parseInt(hexArray[1] + hexArray[2], 16);
    const g = parseInt(hexArray[3] + hexArray[4], 16);
    const b = parseInt(hexArray[5] + hexArray[6], 16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + props.backgroundOpacity + ')'
  }};
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  text-decoration: ${props => props.underline ? 'underline' : undefined};
  font-family: ${props => props.name};
  text-align: ${props => props.yoko};
  align-items: ${props => props.tate};
  writing-mode: ${props => props.direction};
  text-orientation: ${props => props.orientation};
`
type UiPartsLabelProps = {[P in keyof FontType]?: FontType[P]};
const UiPartsLabel = styled.label<UiPartsLabelProps>`
  white-space: nowrap;
  overflow: visible;
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: 'normal';
  text-decoration: ${props => props.underline ? 'underline' : undefined};
  font-family: ${props => props.name};
  pointer-events: none;
  text-align: 'left';
  align-items: 'normal';
  writing-mode: 'horizontal-tb';
`
const UiPartsInputText = styled.input`
  width: inherit;
  padding: .5em 1em;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  pointer-events: none;
`
type UiPartsButtonProps = {
  backgroundColor: string
} & {[P in keyof FontType]?: FontType[P]};
const UiPartsButton = styled.button<UiPartsButtonProps>`
  width: inherit;
  height: inherit;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  background-color: ${props => props.backgroundColor};
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  font-family: ${props => props.name};
`
type CheckboxUiPartsProps = {
  size: number;
}
const CheckboxUiParts = styled.div<CheckboxUiPartsProps>`
  box-sizing: border-box;
  width: ${props => props.size + 'px'};
  height: ${props => props.size + 'px'};
  margin-right: 4px;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
`
const CheckboxCheckUiParts = styled.div<CheckboxUiPartsProps>`
  position: absolute;
  display: block;
  box-sizing: border-box;
  width: ${props => props.size * 0.9 + 'px'};
  height: ${props => props.size / 2 + 'px'};
  top: 12%;
  left: ${props => props.size / 20 + 'px'};
  transform: rotate(-45deg);
  transform-origin: center;
  border-bottom: 4px solid;
  border-left: 4px solid;
  border-color: #000000;
`
const SelectUiParts = styled.select`
  width: inherit;
  padding: .5em 1em;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  pointer-events: none;
`
const MultiUiParts = styled.textarea`
  width: inherit;
  padding: .5em 1em;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  pointer-events: none;
`
type UiPartsImageIconProps = {
  size: number;
}
const UiPartsImageIcon = styled(ImageIcon)<UiPartsImageIconProps>`
  position: absolute;
  left: 50%;
  top: 50%;
  color: #e4e4e4;
  font-size: ${props => (props.size / 2) + 'px'};
  z-index: -1;
  transform: translate(-50%, -50%);
`
type ImageUiPartsProps = {
  radius: number;
}
const VariableImageUiParts = styled.div<ImageUiPartsProps>`
  width: inherit;
  height: inherit;
  overflow: hidden;
  overflow-wrap: anywhere;
  border: solid 4px #000000;
  border-radius: ${props => (props.radius / 2) + '%'};
  box-sizing: border-box;
  display: grid;
  align-items: center;
  justify-items: center;
`
const StaticImageUiParts = styled.img<ImageUiPartsProps>`
  width: inherit;
  height: inherit;
  border-radius: ${props => (props.radius / 2) + '%'};
  box-sizing: border-box;
`
const GroupDiv = styled.div`
  overflow: hidden;
  width: inherit;
  height: inherit;
  border: solid 4px black;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
`
type GroupUiPartsProps = {
  width: number;
  height: number;
}
const GroupUiParts = styled.div<GroupUiPartsProps>`
  position: absolute;
  overflow: hidden;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  border-radius: 10px 50px/50px 10px;
  background-color: #ffffff;
`

type RenderImageProps = {
  item: StaticImageUiSketchItem;
}
const RenderImage: React.FC<RenderImageProps> = ({item}) => {
  const dataUrl = useImageData(item.imageId);

  return (
    <UiPartsStaticImageDiv x={item.x} y={item.y}
      width={item.width} height={item.height}
    >
      <StaticImageUiParts src={dataUrl} draggable="false"
        width={Math.abs(item.width)} height={item.height}
        radius={item.radius} />
    </UiPartsStaticImageDiv>
  );
}

export type RenderItemsProps = {
  parentId: string;
  itemIds: string[];
  items: EditUiCardItemDbHashType;
}
const RenderItems: React.FC<RenderItemsProps> = ({parentId, itemIds, items}) => {
  const ids = itemIds.filter(id => {
    const item = items[id];
    return item && item.parentId === parentId;
  })
  ids.sort((a, b) => {
    const aValue = items[a] as UiCardItemDb;
    const bValue = items[b] as UiCardItemDb;
    return bValue.z - aValue.z;
  })

  const subIds = Object.keys(items).filter(id => {
    const item = items[id];
    return item && item.parentId !== parentId;
  });
  subIds.sort((a, b) => {
    const aValue = items[a] as UiCardItemDb;
    const bValue = items[b] as UiCardItemDb;
    return bValue.z - aValue.z;
  })

  type ChildrenHash = { [key in string]: EditUiCardItemDbHashType }
  const subLists: ChildrenHash = {};
  subIds.forEach(id => {
    const item = items[id];
    if (!item) return;
    if (!subLists[item.parentId]) subLists[item.parentId] = {};
    subLists[item.parentId][id] = item;
  })

  const getFontData = (font: FontType): FontType => {
    let value = deepCopy(font);
    if (value.direction === 'vertical-rl') {
      type YokoType = 'left' | 'center' | 'right';
      type TateType = 'start' | 'center' | 'end';
      type YokoHashType = { [key in TateType]: YokoType };
      type TateHashType = { [key in YokoType]: TateType };
      const yokoHash: YokoHashType = {'start': 'left', 'center': 'center', 'end': 'right'};
      const tateHash: TateHashType = {'left': 'end', 'center': 'center', 'right': 'start'};
      const a = value.yoko;
      const b = value.tate;
      value.yoko = yokoHash[b];
      value.tate = tateHash[a];
    }
    return value;
  }

  return (
    <>
      { ids.map(id => {
        const item = items[id];
        if (!item) return undefined;
        if (item.type === 'input') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <UiPartsInputDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
              labelVisibility={item.labelVisibility} size={size}
            >
              { item.labelVisibility &&
                <UiPartsLabel htmlFor={'input-' + id}
                  size={size} bold={bold} color={color} italic={italic} name={name}
                >
                  {item.label}
                </UiPartsLabel>
              }
              <UiPartsInputText type="text" id={'input-' + id}
                value={item.placeholder} readOnly
              />
            </UiPartsInputDiv>
          );
        } else if (item.type === 'text') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.textFont);
          const labelFont = getFontData(item.labelFont);
          return (
            <UiPartsInputDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
              labelVisibility={item.labelVisibility} size={labelFont.size}
            >
              { item.labelVisibility &&
                <UiPartsLabel htmlFor={'text-' + id}
                  size={labelFont.size} bold={labelFont.bold} color={labelFont.color}
                  italic={false} underline={false} name={labelFont.name}
                  yoko='left' tate='center' direction='horizontal-tb' orientation='mixed'
                >
                  { item.label }
                </UiPartsLabel>
              }
              <TextDiv
                flame={item.flame} flameColor={item.flameColor}
                backgroundOpacity={item.backgroundOpacity} backgroundColor={item.backgroundColor}
                size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
                yoko={yoko} tate={tate} direction={direction} orientation={orientation}
              >
                { item.text }
              </TextDiv>
            </UiPartsInputDiv>
          );
        } else if (item.type === 'button') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <UiPartsDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
            >
              <UiPartsButton backgroundColor={item.backgroundColor}
                size={size} bold={bold} color={color} italic={italic} name={name}
              >
                {item.label}
              </UiPartsButton>
            </UiPartsDiv>
          );
        } else if (item.type === 'checkbox') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <UiPartsCheckboxDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
            >
              <CheckboxUiParts id={'checkbox-' + id} size={item.height} />
              <CheckboxCheckUiParts size={item.height} />
              { item.labelVisibility &&
                <UiPartsLabel htmlFor={'checkbox-' + id}
                  size={size} bold={bold} color={color} italic={italic} name={name}
                >
                  {item.label}
                </UiPartsLabel>
              }
            </UiPartsCheckboxDiv>
          );
        } else if (item.type === 'dropdown') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          const optionIds = Object.keys(item.options);
          return (
            <UiPartsInputDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
              labelVisibility={item.labelVisibility} size={size}
            >
              { item.labelVisibility &&
                <UiPartsLabel htmlFor={'dropdown-' + id}
                  size={size} bold={bold} color={color} italic={italic} name={name}
                >
                  {item.label}
                </UiPartsLabel>
              }
              <SelectUiParts id={'dropdown-' + id}>
                { optionIds.map(id => <option value={id}>{item.options[id]}</option> ) }
              </SelectUiParts>
            </UiPartsInputDiv>
          );
        } else if (item.type === 'multi') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <UiPartsInputDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
              labelVisibility={item.labelVisibility} size={size}
            >
              { item.labelVisibility &&
                <UiPartsLabel htmlFor={'multi-' + id}
                  size={size} bold={bold} color={color} italic={italic} name={name}
                >
                  {item.label}
                </UiPartsLabel>
              }
              <MultiUiParts id={'multi-' + id} rows={6}
                value={item.placeholder} readOnly
              />
            </UiPartsInputDiv>
          );
        } else if (item.type === 'variable') {
          return (
            <UiPartsVariableImageDiv key={id}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
            >
              <VariableImageUiParts radius={item.radius}>
                <UiPartsImageIcon size={Math.min(item.width, item.height)} />
                {item.name}
              </VariableImageUiParts>
            </UiPartsVariableImageDiv>
          );
        } else if (item.type === 'static') {
          return (
            <RenderImage key={id} item={item} />
          );
        } else if (item.type === 'group') {
          const subList = subLists[id];
          const childrenIds = subList ? Object.keys(subList) : [];
          return (
            <UiPartsDiv key={id} style={{overflow: 'hidden'}}
              x={item.x} y={item.y}
              width={item.width} height={item.height}
            >
              <GroupDiv>
                <GroupUiParts width={item.width - 8} height={item.height - 8}>
                { subList &&
                  <RenderItems parentId={id} itemIds={childrenIds} items={subList} />
                }
                </GroupUiParts>
              </GroupDiv>
            </UiPartsDiv>
          );
        } else return undefined;
      }) }
    </>
  );
}

export type UiSketchCardProps = {
  cardId: string;
}
const UiSketchCard: React.FC<UiSketchCardProps> = ({cardId}) => {
  const card = useOneCardDb(cardId);
  const [ids, itemValues] = useUiCardItemDbs(cardId);
  if (!card) return <></>;

  const items: EditUiCardItemDbHashType = {};
  ids.forEach((id, ix) => {
    items[id] = itemValues[ix];
  })

  const cardWidth = card.orientation === 'vertical' ? initial.cardHeight : initial.cardWidth;
  const cardHeight = card.orientation === 'vertical' ? initial.cardWidth : initial.cardHeight;
  const contentWidth = card.orientation === 'vertical' ? initial.uiSketchNarrow : initial.uiSketchWide;
  const contentHeight = card.orientation === 'vertical' ? initial.uiSketchWide : initial.uiSketchNarrow;

  return (
    <CardWrapper width={cardWidth} height={cardHeight}
      $frameDisplay={card.frame.display} $frameColor={card.frame.color}
      scale={initial.cardWidth / initial.uiSketchWide}
    >
      <CardPaper className="magica-card" width={contentWidth} height={contentHeight} scale={initial.cardWidth / initial.uiSketchWide}>
        <RenderItems parentId={cardId} itemIds={ids} items={items} />
      </CardPaper>
    </CardWrapper>
  );
}

export default UiSketchCard;
